import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { partnerAPI } from "@/lib/api";
import { toast } from "sonner";
import { Power, MapPin, Loader2, Navigation } from "lucide-react";

export default function OnlineToggle() {
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);

  // Fetch current availability status
  const fetchAvailability = useCallback(async () => {
    try {
      const response = await partnerAPI.getAvailability();
      setIsOnline(response.data.is_online || false);
      if (response.data.latitude && response.data.longitude) {
        setCurrentLocation({
          latitude: response.data.latitude,
          longitude: response.data.longitude,
        });
      }
    } catch {
      // Default to offline
      setIsOnline(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  // Get current location from browser
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    });
  };

  // Go online with location
  const handleGoOnline = async () => {
    setLocationLoading(true);
    try {
      const location = await getCurrentLocation();
      setCurrentLocation(location);

      await partnerAPI.goOnline({
        latitude: location.latitude,
        longitude: location.longitude,
        is_online: true,
      });

      setIsOnline(true);
      toast.success("You are now online! You will receive nearby delivery requests.");

      // Start location tracking
      startLocationTracking();
    } catch (error) {
      if (error.code === 1) {
        toast.error("Please enable location access to go online");
      } else if (error.code === 2) {
        toast.error("Unable to get your location. Please try again.");
      } else {
        toast.error(error.message || "Failed to go online");
      }
    } finally {
      setLocationLoading(false);
    }
  };

  // Go offline
  const handleGoOffline = async () => {
    setLoading(true);
    try {
      await partnerAPI.goOffline();
      setIsOnline(false);
      toast.success("You are now offline. You will not receive new delivery requests.");
      stopLocationTracking();
    } catch {
      toast.error("Failed to go offline");
    } finally {
      setLoading(false);
    }
  };

  // Location tracking interval
  let locationInterval = null;

  const startLocationTracking = () => {
    // Update location every 30 seconds while online
    locationInterval = setInterval(async () => {
      try {
        const location = await getCurrentLocation();
        setCurrentLocation(location);
        await partnerAPI.updateLocation({
          latitude: location.latitude,
          longitude: location.longitude,
          is_online: true,
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Location update failed:', error.message);
        }
      }
    }, 30000);
  };

  const stopLocationTracking = () => {
    if (locationInterval) {
      clearInterval(locationInterval);
      locationInterval = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => stopLocationTracking();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-secondary">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b border-border ${
      isOnline ? "bg-green-50 dark:bg-green-950" : "bg-secondary"
    }`}>
      <div className={`w-3 h-3 rounded-full ${isOnline ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
      
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${isOnline ? "text-green-700 dark:text-green-300" : "text-muted-foreground"}`}>
          {isOnline ? "Online" : "Offline"}
        </p>
        {isOnline && currentLocation && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            Location tracking active
          </p>
        )}
      </div>

      <Button
        size="sm"
        variant={isOnline ? "destructive" : "default"}
        onClick={isOnline ? handleGoOffline : handleGoOnline}
        disabled={locationLoading}
        className="rounded-none gap-2"
        data-testid="online-toggle-btn"
      >
        {locationLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Getting Location...
          </>
        ) : isOnline ? (
          <>
            <Power className="w-4 h-4" />
            Go Offline
          </>
        ) : (
          <>
            <Navigation className="w-4 h-4" />
            Go Online
          </>
        )}
      </Button>
    </div>
  );
}
